import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/Logo';

const API_BASE = '/api/v1/auth';

type VerificationStatus = 'loading' | 'success' | 'error' | 'no-token';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<VerificationStatus>(token ? 'loading' : 'no-token');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (!token) return;

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API_BASE}/verify-email?token=${token}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success) {
          setStatus('success');
          setMessage(data.message || 'Email verificata con successo!');
        } else {
          setStatus('error');
          setMessage(data.error || 'Errore durante la verifica');
        }
      } catch {
        setStatus('error');
        setMessage('Token non valido o scaduto');
      }
    };

    verifyEmail();
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail.trim()) return;

    setResendStatus('loading');
    try {
      const response = await fetch(`${API_BASE}/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: resendEmail }),
      });
      const data = await response.json();
      if (data.success) {
        setResendStatus('success');
        setResendMessage('Email di verifica inviata! Controlla la tua casella di posta.');
      } else {
        setResendStatus('error');
        setResendMessage(data.error || 'Errore durante l\'invio');
      }
    } catch {
      setResendStatus('error');
      setResendMessage('Errore durante l\'invio');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Logo size="lg" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">
            Health Assistant Tool
          </h1>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Verifica email in corso...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Email Verificata!</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  Il tuo account è ora in attesa di approvazione da parte dell'amministratore.
                  Riceverai una notifica via email quando il tuo account sarà attivo.
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                Vai al Login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verifica Fallita</h2>
              <p className="text-gray-600 mb-6">{message}</p>

              <div className="border-t border-gray-200 pt-6 mt-6">
                <p className="text-sm text-gray-600 mb-4">
                  Il link potrebbe essere scaduto. Inserisci la tua email per ricevere un nuovo link di verifica:
                </p>
                <div className="space-y-3">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="La tua email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleResend}
                    disabled={resendStatus === 'loading' || !resendEmail.trim()}
                    className="w-full py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {resendStatus === 'loading' ? 'Invio in corso...' : 'Reinvia Email di Verifica'}
                  </button>
                  {resendStatus === 'success' && (
                    <p className="text-sm text-green-600 mt-2">{resendMessage}</p>
                  )}
                  {resendStatus === 'error' && (
                    <p className="text-sm text-red-600 mt-2">{resendMessage}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {status === 'no-token' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Token Mancante</h2>
              <p className="text-gray-600 mb-6">
                Nessun token di verifica trovato. Controlla il link nella tua email.
              </p>

              <div className="border-t border-gray-200 pt-6 mt-6">
                <p className="text-sm text-gray-600 mb-4">
                  Non hai ricevuto l'email? Inserisci il tuo indirizzo per ricevere un nuovo link:
                </p>
                <div className="space-y-3">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="La tua email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleResend}
                    disabled={resendStatus === 'loading' || !resendEmail.trim()}
                    className="w-full py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {resendStatus === 'loading' ? 'Invio in corso...' : 'Invia Email di Verifica'}
                  </button>
                  {resendStatus === 'success' && (
                    <p className="text-sm text-green-600 mt-2">{resendMessage}</p>
                  )}
                  {resendStatus === 'error' && (
                    <p className="text-sm text-red-600 mt-2">{resendMessage}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-blue-600 hover:text-blue-500 transition-colors"
            >
              Torna al Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
